import { useState, useCallback, useMemo, useEffect } from 'react';
import type { ExtendedUnitFilters, UnitConfiguration, PropertyUnit } from '@/lib/units';
import { getUnitsCount } from '@/lib/units';
import { loadFiltersFromStorage, saveFiltersToStorage, clearFiltersFromStorage } from '@/lib/filter-validation';

export type BrowseMode = 'featured' | 'configurations' | 'all-units';

export interface FilterCounts {
  all: number;
  '1bed': number;
  '2bed': number;
  townhouse: number;
  lagoon: number;
  canal: number;
  available: number;
}

export const useUnitFilters = (browseMode?: BrowseMode) => {
  const [filters, setFilters] = useState<ExtendedUnitFilters>({});
  const [quickFilter, setQuickFilter] = useState<string>('all');
  const [filtersRestored, setFiltersRestored] = useState(false);

  // Load saved filters on mount
  useEffect(() => {
    if (browseMode && !filtersRestored) {
      const savedFilters = loadFiltersFromStorage(browseMode);
      if (savedFilters && Object.keys(savedFilters).length > 0) {
        setFilters(savedFilters);
        // Set quickFilter based on saved filters
        if (savedFilters.category === '1bed') setQuickFilter('1bed');
        else if (savedFilters.category === '2bed') setQuickFilter('2bed');
        else if (savedFilters.category === 'townhouse') setQuickFilter('townhouse');
        else if (savedFilters.viewType === 'lagoon') setQuickFilter('lagoon');
        else if (savedFilters.viewType === 'canal') setQuickFilter('canal');
        else if (savedFilters.availability === 'available') setQuickFilter('available');
      }
      setFiltersRestored(true);
    }
  }, [browseMode, filtersRestored]);

  // Save filters to localStorage when they change
  useEffect(() => {
    if (browseMode && filtersRestored) {
      saveFiltersToStorage(browseMode, filters);
    }
  }, [filters, browseMode, filtersRestored]);

  const updateFilters = useCallback((newFilters: ExtendedUnitFilters) => {
    setFilters(newFilters);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({});
    setQuickFilter('all');
    if (browseMode) {
      clearFiltersFromStorage(browseMode);
    }
  }, [browseMode]);

  const applyQuickFilter = useCallback((value: string) => {
    setQuickFilter(value);
    const newFilters: ExtendedUnitFilters = {};

    switch (value) {
      case '1bed':
        newFilters.category = '1bed';
        break;
      case '2bed':
        newFilters.category = '2bed';
        break;
      case 'townhouse':
        newFilters.category = 'townhouse';
        break;
      case 'lagoon':
        newFilters.viewType = 'lagoon';
        break;
      case 'canal':
        newFilters.viewType = 'canal';
        break;
      case 'available':
        newFilters.availability = 'available';
        break;
      case 'all':
      default:
        // No filters
        break;
    }

    setFilters(newFilters);
  }, []);

  const activeFilters = useMemo(() => {
    return filters;
  }, [filters]);

  /**
   * Get relevant filters for current browse mode
   * Featured tab only supports category filters
   */
  const getRelevantFilters = useCallback((mode: BrowseMode): ExtendedUnitFilters => {
    if (mode === 'featured') {
      // Featured tab only shows category filters
      return {
        category: filters.category,
        availability: 'available', // Featured always shows available
      };
    }
    // Configurations and All Units show all filters
    return filters;
  }, [filters]);

  /**
   * Get accurate counts for filters based on current context
   * Calculates counts by querying the database with contextual filters
   */
  const getAccurateCounts = useCallback(async (mode: BrowseMode): Promise<FilterCounts> => {
    const baseFilters: ExtendedUnitFilters = { availability: 'available' };
    
    // Helper to count without a specific filter
    const countWithout = (key: keyof ExtendedUnitFilters) => {
      const { [key]: _, ...rest } = { ...baseFilters, ...filters };
      return rest;
    };

    const [allCount, oneBedCount, twoBedCount, townhouseCount, lagoonCount, canalCount] = await Promise.all([
      getUnitsCount(baseFilters),
      getUnitsCount({ ...countWithout('category'), category: '1bed' as const }),
      getUnitsCount({ ...countWithout('category'), category: '2bed' as const }),
      getUnitsCount({ ...countWithout('category'), category: 'townhouse' as const }),
      getUnitsCount({ ...countWithout('viewType'), viewType: 'lagoon' as const }),
      getUnitsCount({ ...countWithout('viewType'), viewType: 'canal' as const }),
    ]);

    return {
      all: allCount.count,
      '1bed': oneBedCount.count,
      '2bed': twoBedCount.count,
      townhouse: townhouseCount.count,
      lagoon: lagoonCount.count,
      canal: canalCount.count,
      available: allCount.count, // Same as all since we always filter by available
    };
  }, [filters]);

  /**
   * Get filtered configurations for Featured tab
   */
  const getFilteredConfigurations = useCallback((
    configurations: UnitConfiguration[],
    mode: BrowseMode
  ): UnitConfiguration[] => {
    if (mode !== 'featured') return configurations;

    // Featured tab: filter by category only
    if (!filters.category) return configurations;

    return configurations.filter(config => {
      const categoryMatch = config.bedrooms === (
        filters.category === '1bed' ? 1 :
        filters.category === '2bed' ? 2 :
        3 // townhouse
      );
      return categoryMatch;
    });
  }, [filters]);

  /**
   * Get filtered units client-side
   */
  const getFilteredUnits = useCallback((units: PropertyUnit[]): PropertyUnit[] => {
    return units.filter(unit => {
      if (filters.category && unit.category !== filters.category) return false;
      if (filters.viewType && unit.view_type !== filters.viewType) return false;
      if (filters.floor && unit.floor !== filters.floor) return false;
      if (filters.block && unit.block !== filters.block) return false;
      if (filters.bedrooms !== undefined && unit.bedrooms !== filters.bedrooms) return false;
      if (filters.bathrooms !== undefined && unit.bathrooms !== filters.bathrooms) return false;
      if (filters.availability && unit.availability !== filters.availability) return false;
      if (filters.minPrice !== undefined && unit.price && unit.price < filters.minPrice) return false;
      if (filters.maxPrice !== undefined && unit.price && unit.price > filters.maxPrice) return false;
      if (filters.minArea !== undefined && unit.area_total && unit.area_total < filters.minArea) return false;
      if (filters.maxArea !== undefined && unit.area_total && unit.area_total > filters.maxArea) return false;
      return true;
    });
  }, [filters]);

  return {
    filters: activeFilters,
    quickFilter,
    updateFilters,
    resetFilters,
    applyQuickFilter,
    getRelevantFilters,
    getAccurateCounts,
    getFilteredConfigurations,
    getFilteredUnits,
  };
};

